import { Field } from '@sitecore-content-sdk/nextjs';

const BreadcrumbQuery = /** GQL */ `query($itemID: String!, $language: String!){
    currentPage: item(path: $itemID, language: $language) {
      ...breadcrumbInfo

      ancestors(hasLayout: true) {
        ...breadcrumbInfo
      }
    }
  }
  
  fragment NavLinks on MultilistField {
    targetItems {
      key: field(name: "key") {
        jsonValue
      }
    }
  }
  fragment breadcrumbInfo on Item {
    Title: field(name: "NavigationTitle") {
      jsonValue
    }
     url {
       path
    }
    navigationFilter: field(name: "navigationFilter") {
      ...NavLinks
    }
  }`;

export default BreadcrumbQuery;

export type BreadcrumbQueryResult = {
  currentPage: BreadcrumbInfo & {
    ancestors: BreadcrumbInfo[];
  };
};

export type BreadcrumbInfo = {
  url: {
    path: string;
  };
  navigationFilter: NavLinks;
  Title: {
    jsonValue: Field<string>;
  };
};

export type NavLinks = {
  targetItems: NavFilter[];
};

export type NavFilter = {
  key: {
    jsonValue: Field<string>;
  };
};

export const BREADCRUMB_DISABLED_LINK_FLAG = 'breadcrumb';

export type BreadcrumbTrailItem = {
  name: string;
  path: string;
};

export function getFilteredBreadcrumbAncestors(
  currentPage: BreadcrumbQueryResult['currentPage'] | null | undefined
): BreadcrumbInfo[] {
  const ancestors = currentPage?.ancestors ?? [];

  return ancestors
    .slice()
    .reverse()
    .filter(
      (ancestor) =>
        Boolean(ancestor?.url?.path?.length) && Boolean(ancestor?.Title?.jsonValue?.value?.length)
    )
    .filter(
      (ancestor) =>
        !ancestor?.navigationFilter?.targetItems?.length ||
        ancestor?.navigationFilter?.targetItems?.find(
          (name) => name?.key?.jsonValue?.value !== BREADCRUMB_DISABLED_LINK_FLAG
        )
    );
}

export function shouldRenderBreadcrumb(
  currentPage: BreadcrumbQueryResult['currentPage'] | null | undefined
): boolean {
  const ancestors = currentPage?.ancestors ?? [];
  const filteredAncestors = getFilteredBreadcrumbAncestors(currentPage);
  return !(filteredAncestors.length === 0 && ancestors.length === 0);
}

export function buildBreadcrumbSchemaTrail(
  currentPage: BreadcrumbQueryResult['currentPage'] | null | undefined,
  currentPageUrlFallback?: string
): BreadcrumbTrailItem[] {
  if (!currentPage || !shouldRenderBreadcrumb(currentPage)) {
    return [];
  }

  const filteredAncestors = getFilteredBreadcrumbAncestors(currentPage);
  const trail: BreadcrumbTrailItem[] = filteredAncestors.map((ancestor) => ({
    name: ancestor.Title!.jsonValue!.value!,
    path: ancestor.url!.path!,
  }));

  const currentName = currentPage.Title?.jsonValue?.value;
  const ancestors = currentPage.ancestors ?? [];

  if (ancestors.length > 0 && currentName) {
    trail.push({
      name: currentName,
      path: currentPage.url?.path || currentPageUrlFallback || '',
    });
  }

  return trail;
}
